/**
 * Threshold-bar geometry for the web report.
 *
 * Port of KinetiQE/src/lib/pose/metricBands.ts. The desktop app computes and
 * grades every angle before it reaches Firestore, so this copy exists only to
 * lay out the bar — it must not re-decide a verdict.
 *
 * The numbers below (NORMAL_RANGES, SEVERITY_CUTS, the grading thresholds) are
 * duplicated from `KinetiQE/src/lib/pose/angles.ts`. They are the fallback for
 * a scan document written before `normalRange`/`severity` were persisted on
 * each angle; for any recent scan the stored values win. A bar drawn on stale
 * bands would disagree with the badge printed beside it, so the duplication is
 * held to the source by `KinetiQE/src/lib/pose/webReportParity.test.ts`, which
 * imports this file and fails if the two ever diverge.
 */

export const SEVERITY_COLORS = {
  severe: '#ef4444',
  moderate: '#f97316',
  mild: '#eab308',
  normal: '#22c55e',
};

export const NORMAL_RANGES = {
  'Shoulder tilt': [0, 3],
  'Pelvic obliquity': [0, 3],
  'Spine deviation': [0, 4],
  'Trunk lean': [0, 5],
  'Forward head': [0, 8],
  'Left knee deviation': [0, 8],
  'Right knee deviation': [0, 8],
  'Left knee flexion': [160, 185],
  'Right knee flexion': [160, 185],
  'Left elbow angle': [160, 185],
  'Right elbow angle': [160, 185],
  'Left calcaneal angle': [0, 14],
  'Right calcaneal angle': [0, 14],
  'Left shin rotation': [0, 6],
  'Right shin rotation': [0, 6],
  'Heel symmetry': [0, 3],
  'Head tilt': [0, 3],
  'Head center deviation': [0, 4],
  'Body midline deviation': [0, 3],
  'Pelvic shift': [0, 4],
  'Shoulder-to-pelvis relationship': [0, 4],
  'Arm symmetry': [0, 6],
  'Left leg tilt': [0, 5],
  'Right leg tilt': [0, 5],
  'Leg tilt asymmetry': [0, 3],
  'Left foot progression': [0, 15],
  'Right foot progression': [0, 15],
  'Craniovertebral angle': [50, 95],
  'Cervical flexion/extension': [-25, 10],
  'Forward shoulder': [0, 4],
  'Hip position': [0, 3],
  'Pelvic-to-trunk relationship': [165, 185],
};

export const DEFAULT_SEVERITY_CUTS = { moderate: 5, severe: 9 };

export const SEVERITY_CUTS = {
  'Left calcaneal angle': { moderate: 3, severe: 5 },
  'Right calcaneal angle': { moderate: 3, severe: 5 },
  'Left shin rotation': { moderate: 5, severe: 15 },
  'Right shin rotation': { moderate: 5, severe: 15 },
};

/** Worst-first, so a summary can order findings by how much they matter. */
export const SEVERITY_ORDER = ['severe', 'moderate', 'mild', 'normal'];

/** Ranks a severity; higher is worse. */
export function severityRank(severity) {
  const i = SEVERITY_ORDER.indexOf(severity);
  return i === -1 ? 0 : SEVERITY_ORDER.length - 1 - i;
}

export function severityColor(severity) {
  return SEVERITY_COLORS[severity] ?? SEVERITY_COLORS.normal;
}

export function severityCutsFor(name) {
  return SEVERITY_CUTS[name] ?? DEFAULT_SEVERITY_CUTS;
}

export function gradeSeverity(value, range, cuts = DEFAULT_SEVERITY_CUTS) {
  const [lo, hi] = range;
  const deviation = value < lo ? lo - value : value > hi ? value - hi : 0;
  if (deviation === 0) return 'normal';
  if (deviation < cuts.moderate) return 'mild';
  if (deviation < cuts.severe) return 'moderate';
  return 'severe';
}

/**
 * Degrees past the "severe" threshold that the bar keeps drawing. Grading has
 * no upper bound, so the track needs a finite end; six degrees of red keeps the
 * worst readings inside the bar rather than pinned to its edge.
 */
const SEVERE_SPAN = 6;

function boundaries(cuts) {
  return {
    mildTo: cuts.moderate,
    moderateTo: cuts.severe,
    severeTo: cuts.severe + SEVERE_SPAN,
  };
}

function band(severity, from, to) {
  return { severity, from, to, color: SEVERITY_COLORS[severity] };
}

/**
 * Build the bar for a metric's normal range.
 *
 * Ranges anchored at zero (deviation metrics: shoulder tilt, pelvic obliquity)
 * get a one-sided bar — a negative deviation does not exist. Ranges with a
 * non-zero floor (joint angles like knee flexion, normal 160–185°) get a
 * symmetric bar, because falling short of the range is as clinically real as
 * exceeding it.
 */
export function bandScaleFor(range, cuts = DEFAULT_SEVERITY_CUTS) {
  const [lo, hi] = range;
  const { mildTo: MILD_TO, moderateTo: MODERATE_TO, severeTo: SEVERE_TO } = boundaries(cuts);
  const bands = [];

  if (lo > 0) {
    bands.push(
      band('severe', lo - SEVERE_TO, lo - MODERATE_TO),
      band('moderate', lo - MODERATE_TO, lo - MILD_TO),
      band('mild', lo - MILD_TO, lo),
    );
  }

  bands.push(
    band('normal', lo, hi),
    band('mild', hi, hi + MILD_TO),
    band('moderate', hi + MILD_TO, hi + MODERATE_TO),
    band('severe', hi + MODERATE_TO, hi + SEVERE_TO),
  );

  const min = bands[0].from;
  const max = bands[bands.length - 1].to;
  const ticks = [min, ...bands.map((b) => b.to)];

  return { min, max, bands, ticks };
}

/** Where a value sits along the bar, clamped to 0–1 so the pin stays on track. */
export function positionOn(scale, value) {
  const span = scale.max - scale.min;
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (value - scale.min) / span));
}

/**
 * The severity to draw for a stored angle.
 *
 * Prefers the value the desktop app already graded and persisted. Recomputing
 * is the fallback for older scan documents; if a range is unknown on both
 * sides there is nothing to draw and the caller renders no bar.
 */
export function severityForAngle(angle) {
  if (angle?.severity) return angle.severity;
  const range = rangeForAngle(angle);
  if (!range) return 'normal';
  return gradeSeverity(angle.degrees, range, severityCutsFor(angle.name));
}

/** The normal range for a stored angle: persisted first, table as fallback. */
export function rangeForAngle(angle) {
  if (!angle) return null;
  if (Array.isArray(angle.normalRange) && angle.normalRange.length === 2) {
    return angle.normalRange;
  }
  return NORMAL_RANGES[angle.name] ?? null;
}

/** Clinician-facing wording for a severity, matching the reference report. */
export function severityLabel(severity) {
  switch (severity) {
    case 'normal':
      return 'Normal';
    case 'mild':
      return 'Milder';
    case 'moderate':
      return 'Moderate';
    case 'severe':
      return 'Serious';
    default:
      return 'Normal';
  }
}

/**
 * Bilateral bars (heel inversion/eversion, tibial/fibular torsion) run outward
 * from a shared zero in the middle, with one side per direction. Both halves
 * use the same band widths as the one-sided bar, so a 10° heel tilt reads the
 * same severity whichever direction it leans.
 */
export function bilateralScaleFor(range, cuts = DEFAULT_SEVERITY_CUTS) {
  const hi = range[1];
  const { mildTo: MILD_TO, moderateTo: MODERATE_TO, severeTo: SEVERE_TO } = boundaries(cuts);
  const bands = [
    band('normal', 0, hi),
    band('mild', hi, hi + MILD_TO),
    band('moderate', hi + MILD_TO, hi + MODERATE_TO),
    band('severe', hi + MODERATE_TO, hi + SEVERE_TO),
  ];
  return { half: hi + SEVERE_TO, bands, ticks: bands.map((b) => b.to) };
}

/**
 * Signed position of a value on a bilateral bar, 0–1 across the whole track.
 * `side` is which direction label the finding belongs under: -1 draws left of
 * centre, +1 right, 0 sits on centre.
 */
export function bilateralPosition(scale, degrees, side) {
  const clamped = Math.min(scale.half, Math.abs(degrees));
  return 0.5 + (side * clamped) / (scale.half * 2);
}
